<script lang="ts">
  import type { PageProps } from './$types'
  import { tick } from 'svelte'
  import { resolve } from '$app/paths'
  import SEO from '#lib/components/SEO.svelte'
  import CTA from '#lib/components/CTA.svelte'
  import JsonLd from '#lib/components/JsonLd.svelte'
  import ScrollArrows from '#lib/components/ScrollArrows.svelte'

  let { data }: PageProps = $props()
  const episodes = $derived(data.episodes)

  // Which episode index is playing (null = none playing yet, all tiles are posters)
  let playing: number | null = $state(null)
  const tileEls: (HTMLElement | undefined)[] = $state([])

  async function play(index: number) {
    playing = index
    await tick()
    tileEls[index]?.focus()
  }

  // Horizontal-scroll slider; boundary-aware arrows via shared ScrollArrows.
  let scrollEl: HTMLDivElement | undefined = $state()
</script>

<SEO
  title="Taghriba — a Palestinian drama series"
  description="Taghriba is a 31 episode Palestinian drama series following one family before, during and after the Nakba. Presented in English by the PAPA Art Foundation."
  type="article"
/>

<article class="bg-brand-sand-light">
  <!-- Hero + episodes share one continuous diagonal green gradient -->
  <div class="from-brand-green to-brand-green-deep bg-linear-to-br text-white">
    <!-- Title band — top padding only; the slider owns the gap below it -->
    <section>
      <div
        class="container mx-auto px-5 pt-16 pb-0 text-center [text-shadow:0_1px_3px_rgb(0_0_0/0.35)] lg:pt-24"
      >
        <p class="font-display mb-4 text-sm tracking-widest uppercase opacity-70">A drama series</p>
        <h1 class="font-display mb-6 text-5xl font-bold lg:text-8xl">Taghriba</h1>
        <p class="mx-auto mb-4 max-w-2xl text-lg opacity-80">
          A 31 episode Palestinian drama series, written by Walid Seif and directed by Hatem Ali
          (2004), following one family before, during and after the Nakba.
        </p>
        <p class="mx-auto mb-4 max-w-2xl text-base opacity-80">
          Presented in English by the PAPA Art Foundation, with translation by a Gaza based academic
          translator and commentary by the PAPA Archive Team.
        </p>
        <p class="mx-auto max-w-2xl text-base italic opacity-80">
          Dedicated to the memory of Professor Refaat Alareer.
        </p>
      </div>
    </section>

    <!-- Episodes slider: facade tiles → iframe on click, deep-link anchors #ep-N -->
    <section class="relative">
      <!-- Equal top/bottom padding around the slider row; scroller side
           padding (px) == inter-tile gap so the left inset matches the gaps. -->
      <div class="relative py-12 lg:py-16">
        <!-- A scroll container must be keyboard-focusable so arrow keys can scroll it (WCAG 2.1.1). -->
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <div
          bind:this={scrollEl}
          role="region"
          aria-label="Episodes"
          tabindex="0"
          class="taghriba-scroll flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 focus:ring-2 focus:ring-white focus:outline-none lg:scroll-px-20 lg:gap-8 lg:px-20"
        >
          {#each episodes as ep, i (ep.id)}
            <article
              id="ep-{i + 1}"
              bind:this={tileEls[i]}
              tabindex="-1"
              class="group relative aspect-video w-[85vw] flex-shrink-0 snap-start overflow-hidden outline-none lg:w-[55vw]"
            >
              {#if playing === i}
                <iframe
                  src={`https://player.vimeo.com/video/${ep.id}?autoplay=1&dnt=1&title=0&byline=0&portrait=0`}
                  title={ep.title}
                  allow="autoplay; fullscreen; picture-in-picture"
                  class="absolute inset-0 h-full w-full border-0 bg-black"
                ></iframe>
              {:else}
                <button
                  type="button"
                  onclick={() => play(i)}
                  aria-label={`Play Episode ${String(i + 1)}`}
                  data-umami-event="taghriba-play"
                  data-umami-event-episode={i + 1}
                  class="absolute inset-0 h-full w-full cursor-pointer bg-neutral-900 text-white transition-colors hover:bg-neutral-800"
                >
                  <span
                    class="font-display absolute top-6 left-6 text-left text-3xl font-bold lg:top-10 lg:left-10 lg:text-6xl"
                  >
                    Episode {i + 1}
                  </span>
                  <span
                    class="bg-brand-sand absolute right-4 bottom-4 flex h-12 w-12 items-center justify-center rounded-full shadow-2xl transition-transform group-hover:scale-110 lg:right-10 lg:bottom-10 lg:h-24 lg:w-24"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      class="ml-0.5 h-6 w-6 text-neutral-900 lg:ml-1 lg:h-12 lg:w-12"
                      aria-hidden="true"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </button>
              {/if}
            </article>
          {/each}
        </div>

        <!-- Prev / next arrows: shared site-wide treatment -->
        <ScrollArrows
          target={scrollEl}
          theme="dark"
          labelPrev="Previous episodes"
          labelNext="More episodes"
        />
      </div>
    </section>
  </div>

  <!-- Episodes 1–4 are the free preview; 5–31 live on the Ally page -->
  <section class="border-b border-black/10">
    <div class="container mx-auto px-5 py-16 text-center lg:py-20">
      <h2 class="font-display mx-auto mb-6 max-w-3xl text-3xl font-bold text-black lg:text-5xl">
        Episodes 5 to 31 are for PAPA Allies.
      </h2>
      <p class="mx-auto mb-10 max-w-2xl text-lg text-black/80">
        Already an Ally? Enter the access code from your welcome email.
      </p>
      <div class="flex flex-wrap justify-center gap-4">
        <CTA
          href={resolve('/taghriba/ally')}
          text="Enter Ally access code"
          data-umami-event="taghriba-ally-enter-cta"
        />
        <CTA
          href={resolve('/donations')}
          text="Become an Ally"
          variant="secondary"
          data-umami-event="taghriba-ally-join-cta"
        />
      </div>
    </div>
  </section>

  <section class="border-b border-black/10">
    <div class="container mx-auto px-5 py-16 text-center lg:py-20">
      <h2 class="font-display mx-auto mb-6 max-w-3xl text-3xl font-bold text-black lg:text-5xl">
        Host Taghriba in your community
      </h2>
      <p class="mx-auto max-w-2xl text-lg text-black/80">
        Coming soon for our PAPA members: the PAPA Taghriba Host Kit, everything you need to screen
        the series in a classroom, a community hall or your living room. An episode guide,
        discussion questions, a translation glossary, and guidance for holding the hard
        conversations with care. No expertise needed.
      </p>
    </div>
  </section>

  <!-- Interstitial CTA → routes to the site's dedicated donations page -->
  <section class="border-y border-black/10">
    <div class="container mx-auto px-5 py-16 text-center lg:py-20">
      <h2 class="font-display mx-auto mb-6 max-w-3xl text-3xl font-bold text-black lg:text-5xl">
        Every translated episode is supported by people like you.
      </h2>
      <p class="mx-auto mb-10 max-w-2xl text-lg text-black/80">
        Your monthly support is how the series reaches new audiences.
      </p>
      <CTA
        href={resolve('/donations')}
        text="Support Taghriba"
        data-umami-event="taghriba-support-cta-click"
      />
    </div>
  </section>
</article>

<JsonLd
  data={{
    '@context': 'https://schema.org',
    '@type': 'TVSeries',
    name: 'Taghriba',
    description:
      'A 31 episode Palestinian drama series following one family before, during and after the Nakba.',
    numberOfEpisodes: 31,
    author: { '@type': 'Person', name: 'Walid Seif' },
    director: { '@type': 'Person', name: 'Hatem Ali' },
    datePublished: '2004',
    inLanguage: 'ar',
    episode: episodes.map((ep, i) => ({
      '@type': 'TVEpisode',
      episodeNumber: i + 1,
      name: ep.title,
      url: `https://vimeo.com/${ep.id}`
    }))
  }}
/>

<style>
  .taghriba-scroll::-webkit-scrollbar {
    display: none;
  }
  .taghriba-scroll {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
